/**
 * `<EvaluationCards>` — Recommendation card + Detailed Assessment split
 * panel for the AI Evaluation tab (Batch 30j-3).
 *
 * Pure render. Composes existing primitives:
 *   - `<CollapsibleCard>` (foundations §9.16)
 *   - `<SplitPanel>` (foundations §9.15)
 *   - `<VerdictBadge>` (foundations §9.10)
 *   - `<EvaluationMarkdown>` (Batch 30j-3)
 *
 * Spec: `frontend/docs/30j_3_design.md` §4 + `frontend/docs/30j_3_architect.md` §5.2.
 *
 * Active section is `useState`, NOT URL state (Architect §3.3). When the
 * `parsed.navItems` reference changes (regenerate path), the active key
 * resets to the first nav item.
 *
 * Section-color signal is conveyed by:
 *   - The detail label color (small caps uppercase) — `detailLabelColor` prop
 *   - The active nav-item left-border + icon color
 *   - The label TEXT itself (NOT the body) — body markdown stays
 *     `text-text-secondary` per HoD §6.2.
 *
 * Per Sec Lead HARD-9: section color is an ACCENT, not the only signal.
 * The label text is the primary identifier (WCAG 1.4.1 — color is not
 * the only means of conveying information).
 */
import { useEffect, useState, type JSX } from "react";

import { CollapsibleCard } from "@/components/common/CollapsibleCard";
import { SplitPanel } from "@/components/common/SplitPanel";
import { VerdictBadge } from "@/components/candidates/VerdictBadge";

import { EvaluationMarkdown } from "@/components/candidate-page/EvaluationMarkdown";
import type { ParsedEvaluation } from "@/lib/parseEvaluation";

interface EvaluationCardsProps {
  parsed: ParsedEvaluation;
}

/**
 * Pick the section the Detailed Assessment split-panel opens on. Prefer the
 * first section whose body carries a `[[JD_ANCHOR: …]]` quote-rule so the
 * "AI shows its work" blue anchor rows are visible on FIRST PAINT — the split
 * panel renders only the active section, and the canonical eval opens with
 * "Candidate Overview" (no anchors) followed by anchor-bearing sections
 * (Strengths, Gaps, …). Without this, a fresh page load / screenshot shows
 * zero `jd-anchor` rows even though the eval is fully anchored (the showcase
 * hero-shot bug). Falls back to the first nav item for anchor-less evals
 * (legacy / prompt drift) so behaviour is unchanged when there is nothing to
 * surface.
 */
function initialSectionKey(parsed: ParsedEvaluation): string {
  for (const item of parsed.navItems) {
    const body = parsed.sections[parsed.rawKeyByKey[item.key] ?? ""] ?? "";
    if (body.includes("[[JD_ANCHOR:")) return item.key;
  }
  return parsed.navItems[0]?.key ?? "";
}

export function EvaluationCards({
  parsed,
}: EvaluationCardsProps): JSX.Element {
  const [activeKey, setActiveKey] = useState<string>(() =>
    initialSectionKey(parsed),
  );

  // Reset active key when the parsed evaluation changes (regenerate path emits
  // a fresh ParsedEvaluation; the reference flips). Re-derives the anchored
  // default section.
  useEffect(() => {
    setActiveKey(initialSectionKey(parsed));
  }, [parsed]);

  const activeBody =
    activeKey && parsed.sections[parsed.rawKeyByKey[activeKey] ?? ""]
      ? parsed.sections[parsed.rawKeyByKey[activeKey] ?? ""] ?? ""
      : "";
  const activeRawKey = parsed.rawKeyByKey[activeKey] ?? activeKey;
  const activeColor =
    parsed.colorByKey[activeKey] ?? "var(--section-overview)";

  // Fallback path (no `## H2` sections at all) — render the analysis as
  // a single full-width markdown block inside a plain card frame.
  if (
    parsed.navItems.length === 0 &&
    parsed.recommendationBody === null &&
    parsed.fallbackBody !== null
  ) {
    return (
      <div className="space-y-3 px-content-px py-5" data-testid="ai-eval-cards">
        <CollapsibleCard title="AI Evaluation" defaultOpen={true}>
          <EvaluationMarkdown source={parsed.fallbackBody} />
        </CollapsibleCard>
      </div>
    );
  }

  return (
    <div className="space-y-3 px-content-px py-5" data-testid="ai-eval-cards">
      {parsed.recommendationBody ? (
        <CollapsibleCard
          title="Pre-Interview Recommendation"
          defaultOpen={true}
          borderColor={parsed.verdictColor}
          trailingBadge={
            parsed.verdictNormalized ? (
              <VerdictBadge verdict={parsed.verdictNormalized} size="sm" />
            ) : null
          }
        >
          <EvaluationMarkdown source={parsed.recommendationBody} />
        </CollapsibleCard>
      ) : null}

      {parsed.navItems.length > 0 ? (
        <CollapsibleCard title="Detailed Assessment" defaultOpen={true}>
          <SplitPanel
            navItems={parsed.navItems}
            activeKey={activeKey}
            onSelect={setActiveKey}
            detailLabel={activeRawKey}
            detailLabelColor={activeColor}
            detail={<EvaluationMarkdown source={activeBody} />}
            height="420px"
          />
        </CollapsibleCard>
      ) : null}
    </div>
  );
}
